/* eslint-disable no-use-before-define */
import React, { memo, useCallback } from 'react'
import { Box, Checkbox, InputBase, Skeleton, SxProps } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro'
import { DayDataTypes } from '../../../../../../api/models/dataList.model'
import { RestrictionTypeListType } from '../../../../../../api/models/restrictionTypeList.model'
import RatesGridTypeAvailablesCell from './components/RatesGridTypeAvailablesCell'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from '../../contexts/ratesDataGrid/RatesDataGridContext.types'
import { useRatesDataGridContext } from '../../contexts/ratesDataGrid/RatesDataGridContext'
import { dateUtils } from 'src/utils/date.util'
interface Props {
  params: GridRenderEditCellParams
  openLegendModal: () => void
  dayDate: Date
  onBeforeEdit: () => void
}

const DATA_TYPE_MAP: {
  [key in keyof Required<DayDataTypes>]: RestrictionTypeListType
} = {
  price: RestrictionTypeListType.Number,
  closed: RestrictionTypeListType.Boolean,
  maxAnticipation: RestrictionTypeListType.Time,
  closedOnArrival: RestrictionTypeListType.Boolean,
  closedOnDeparture: RestrictionTypeListType.Boolean,
  exactStay: RestrictionTypeListType.Number,
  exactStayArrival: RestrictionTypeListType.Number,
  maximumStay: RestrictionTypeListType.Number,
  maximumStayArrival: RestrictionTypeListType.Number,
  minimumStay: RestrictionTypeListType.Number,
  minimumStayArrival: RestrictionTypeListType.Number,
  release: RestrictionTypeListType.Time,
  roomToSell: RestrictionTypeListType.Number,
  availables: RestrictionTypeListType.Number
}

const RatesGridEditCellRender: ReactFC<Props> = ({
  params,
  openLegendModal,
  dayDate,
  onBeforeEdit
}) => {
  const { setEditDateData, editedDaysList } = useRatesDataGridContext()
  const row = params.row as RatesDataGridRow
  const dateKey = dateUtils.formatDateToString(dayDate)
  const dayDataType = 'dayDataType' in row ? row.dayDataType : 'price'
  const editedValue = editedDaysList[row.id]?.[dateKey]?.[dayDataType]
  const isEdited = Object.hasOwn(editedDaysList[row.id] ?? {}, dateKey)
  const inputVal =
    editedValue ??
    ('data' in row && 'dayDataByDate' in row.data
      ? row.data.dayDataByDate[dateKey]?.[dayDataType]
      : undefined)

  const handleInputChange = useCallback(
    (value: number | string | boolean) => {
      onBeforeEdit()
      const rateRow = row as RateRow
      setEditDateData({
        date: dayDate,
        dayData: {
          [rateRow.dayDataType]: value
        },
        row: rateRow
      })
    },
    [dayDate, onBeforeEdit, row, setEditDateData]
  )

  if (row.type.includes('Skeleton')) {
    return <Skeleton variant='rectangular' width='100%' height='60%' />
  }

  if (row.type === RowType.ROOM_TYPE) {
    return (
      <RatesGridTypeAvailablesCell
        availabilityData={row.data.availabilityDataByDate[dateKey]}
        onClick={() => openLegendModal()}
      />
    )
  }

  if (
    row.type !== RowType.CAPACITY &&
    row.type !== RowType.RATE &&
    row.type !== RowType.CHANNEL
  )
    return null

  const rateRow = row as RateRow | CapacityRow | ChannelRow
  const type = DATA_TYPE_MAP[rateRow.dayDataType] || DATA_TYPE_MAP.price

  const editable = row.editable || row.dayDataType !== 'price'

  const isFatherRow = !row.inherited && !row.fatherId

  return (
    <Box
      className={isEdited ? 'editedCell' : undefined}
      onFocusCapture={onBeforeEdit}
      onPointerDownCapture={onBeforeEdit}
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        width: '100%',
        '& *': {
          ...(!editable &&
            (row.type === RowType.CHANNEL ||
              row.type === RowType.RATE ||
              row.type === RowType.CAPACITY) && {
              cursor: 'not-allowed !important'
            })
        }
      }}
    >
      {
        {
          [RestrictionTypeListType.Boolean]: (
            <EditCheckbox
              checked={!!inputVal as boolean}
              disabled={!editable}
              onChange={handleInputChange}
            />
          ),
          [RestrictionTypeListType.Time]: (
            <EditInput
              value={inputVal?.toString() ?? ''}
              disabled={!editable}
              onChange={handleInputChange}
              onlyInt
              {...(isFatherRow && {
                sx: {
                  fontWeight: 600
                }
              })}
            />
          ),
          [RestrictionTypeListType.Number]: (
            <EditInput
              value={inputVal?.toString() ?? ''}
              disabled={!editable}
              onChange={handleInputChange}
              {...(isFatherRow && {
                sx: {
                  fontWeight: 600
                }
              })}
            />
          )
        }[type]
      }
    </Box>
  )
}

const EditCheckbox = ({
  onChange,
  disabled,
  checked
}: {
  onChange: (value: boolean) => void
  disabled: boolean
  checked: boolean
}) => {
  return (
    <Checkbox
      disabled={disabled}
      checked={checked}
      onChange={(e) => {
        const newChecked = e.target.checked
        onChange(newChecked)
      }}
    />
  )
}

const EditInput = ({
  disabled,
  onChange,
  onlyInt,
  sx,
  value
}: {
  disabled: boolean
  onChange: (value: number | string) => void
  onlyInt?: boolean
  sx?: SxProps
  value: string
}) => {
  // useEffect(() => {
  //   setInputVal(defaultValue)
  // }, [defaultValue, ratesStateData])

  return (
    <InputBase
      sx={{
        '& > *': {
          textAlign: 'center'
        },
        ...sx
      }}
      disabled={disabled}
      onChange={(e) => {
        const newVal = onlyInt
          ? e.target.value.replace(/[^\d]/gi, '')
          : e.target.value.replace(/[^\d.]/gi, '')
        onChange(newVal)
      }}
      value={value}
    />
  )
}

// const EditTime = () => {
//   const [timeVal, setTimeVal] = useState<Date | null>(null)
//   return (
//     <LocalizationProvider
//       dateAdapter={AdapterMoment}
//       locale='es'
//     >
//       <MobileTimePicker
//         onChange={(newValue) => {
//           setTimeVal(newValue)
//         }}
//         value={timeVal}
//         renderInput={(params) => (
//           <InputBase
//             {...(params as any)}
//             sx={{
//               '& > *': {
//                 textAlign: 'center'
//               }
//             }}
//           />
//         )}
//       />
//     </LocalizationProvider>
//   )
// }

export default memo(RatesGridEditCellRender)
